
import {
  defineComponent,
  computed,
  ref,
  watch,
  reactive,
  shallowRef,
  onMounted,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ContactOption from '@/interfaces/ContactOption';
import GridRow from '@/components/grid/GridRow.vue';
import GridCol from '@/components/grid/GridCol.vue';

export default defineComponent({
  components: { GridCol, GridRow },
  props: {
    categories: {
      type: String,
      required: false,
      default: () => 'all',
    },
  },

  setup(props) {
    const store = useStore();
    const navRow = ref<HTMLElement>();
    const route = useRoute();

    const contactOptionsByCategories = computed<Record<string, ContactOption[]>>(
      () => store.getters['contactOptions/get'](props.categories.split(',')),
    );

    const activeContent = reactive({
      component: shallowRef(defineComponent({
        template: '<div class="container-fluid"></div>',
      })),
    });

    const contactOptionsAll = computed<ContactOption[]>(() => {
      let items: ContactOption[] = [];

      Object.keys(contactOptionsByCategories.value).forEach((key: string) => {
        items = [
          ...items,
          ...contactOptionsByCategories.value[key],
        ];
      });

      return items;
    });

    const activeOptionId = ref(0);

    const activeOption = computed(() => contactOptionsAll.value.filter(
      (co) => co.id === activeOptionId.value,
    )[0]);

    const animateScrollTo = (to: string | number, scrollToOffset = 0, duration = 575): Promise<boolean> => new Promise((resolve, reject) => {
      let scrollTo = 0;
      let offset = scrollToOffset;

      if (typeof to === 'string') {
        // Assuming this is a selector we can use to find an element
        const scrollToObj = document.querySelector(to);

        if (scrollToObj && typeof scrollToObj.getBoundingClientRect === 'function') {
          scrollTo = window.pageYOffset + scrollToObj.getBoundingClientRect().top;
        }
      } else {
        scrollTo = to;
      }

      const header = document.querySelector('header');

      if (header) {
        offset += header.scrollHeight;
      }

      const wpAdminBar = document.querySelector('#wpadminbar');

      if (wpAdminBar) {
        offset += wpAdminBar.scrollHeight;
      }

      if (scrollTo > offset) {
        scrollTo -= offset;
      }

      if (scrollTo < 0) {
        scrollTo = 0;
      }

      if (!document.scrollingElement || document.scrollingElement.scrollTop === scrollTo) {
        reject(new Error('no need of scrolling'));
      } else {
        const cosParameter = (window.pageYOffset - scrollTo) / 2;
        let scrollCount = 0;
        let oldTimestamp = window.performance.now();
        let breakAnimation = false;

        const step = (newTimestamp: number) => {
          let tsDiff = newTimestamp - oldTimestamp;

          if (tsDiff > 100) {
            tsDiff = 30;
          }

          scrollCount += Math.PI / (duration / tsDiff);

          const moveStep = Math.round(scrollTo + cosParameter + cosParameter * Math.cos(scrollCount));
          window.scrollTo(0, moveStep);
          oldTimestamp = newTimestamp;

          if (scrollCount >= Math.PI) {
            breakAnimation = true;
          }

          if (!breakAnimation) {
            window.requestAnimationFrame(step);
          } else {
            resolve(true);
          }
        };

        window.requestAnimationFrame(step);
      }
    });

    const changeActiveOptionId = (id: number) => {
      activeOptionId.value = id;

      nextTick(() => {
        window.location.hash = `#${activeOption.value.slug}`;
      });
    };

    const scrollToActiveOption = () => {
      let offset = 0;

      if (navRow.value) {
        const {
          flexWrap,
        } = getComputedStyle(navRow.value);

        if (flexWrap === 'nowrap') {
          offset += navRow.value?.scrollHeight || 0;
        }
      }

      animateScrollTo(`#contact-option-${activeOption.value.id}`, offset);
    };

    watch(() => route.hash, () => {
      const hashOption = contactOptionsAll.value.find((contactOption) => contactOption.slug === `${route.hash || ''}`.trim().substr(1));

      if (hashOption) {
        nextTick(() => {
          scrollToActiveOption();
        });
      }
    });

    watch(() => activeOption.value, () => {
      activeContent.component = defineComponent({
        template: `<div class="container-fluid">${activeOption.value.content}</div>`,
      });
    });

    const setDefaultOption = () => {
      let initialOption = contactOptionsAll.value[0] as ContactOption | undefined;
      let scroll = false;

      if (`${route.hash || ''}`.trim().length > 0) {
        const optionSlug = `${route.hash || ''}`.trim().substr(1);
        const hashOption = contactOptionsAll.value.find((contactOption) => contactOption.slug === optionSlug);

        if (hashOption) {
          changeActiveOptionId(hashOption.id);

          initialOption = hashOption;

          scroll = true;
        }
      }

      activeOptionId.value = initialOption?.id || 0;

      if (scroll) {
        nextTick(() => {
          scrollToActiveOption();
        });
      }
    };

    watch(() => contactOptionsAll.value, () => {
      setDefaultOption();
    });

    onMounted(() => {
      store.dispatch('contactOptions/get', props.categories.split(','));
    });

    return {
      navRow,
      contactOptionsByCategories,
      contactOptionsAll,
      changeActiveOptionId,
      activeOptionId,
      activeOption,
      activeContent,
      route,
    };
  },
});
